import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TextInput, Typography } from '@guildeducationinc/recess';
import { Checkbox, FormControlLabel } from '@mui/material';
import { DeadlineInput } from '@src/components/Admin/PolicyBuilder/DeadlineInput';
import { WorkflowType } from '@src/types';
import { camelCase } from 'lodash';
import { DropdownSelect, InfoPopover, useLocale } from 'pe-core-frontend';
import { useMemo } from 'react';
export const ApprovalPolicy = ({ policyConfig, type, readonly, workflowOptions, onChange, }) => {
    const { t } = useLocale();
    const camelCasedType = camelCase(type);
    const includeTermStart = [
        WorkflowType.PROGRAM_APPROVAL,
        WorkflowType.PAYMENT_REQUEST_APPROVAL,
        WorkflowType.PREIMBURSEMENT_APPROVAL,
    ].includes(type);
    const includeDeadline = [
        WorkflowType.PROGRAM_APPROVAL,
        WorkflowType.PAYMENT_REQUEST_APPROVAL,
        WorkflowType.PREIMBURSEMENT_APPROVAL,
    ].includes(type);
    const includeDocumentDeadline = [
        WorkflowType.PAYMENT_REQUEST_APPROVAL,
        WorkflowType.PREIMBURSEMENT_APPROVAL,
    ].includes(type);
    const includeOptionalDocument = [
        WorkflowType.PAYMENT_REQUEST_APPROVAL,
        WorkflowType.PREIMBURSEMENT_APPROVAL,
    ].includes(type);
    const deadlineConfig = useMemo(() => {
        return {
            min: {
                days: policyConfig.deadline?.minInterval?.days,
                years: policyConfig.deadline?.minInterval?.years,
                backward: policyConfig.deadline?.minInterval?.backward ?? false,
                basedOn: policyConfig.deadline?.minIntervalBasedOn,
            },
            max: {
                days: policyConfig.deadline?.maxInterval?.days,
                years: policyConfig.deadline?.maxInterval?.years,
                backward: policyConfig.deadline?.maxInterval?.backward ?? false,
                basedOn: policyConfig.deadline?.maxIntervalBasedOn,
            },
        };
    }, [policyConfig.deadline]);
    const documentDeadlineConfig = useMemo(() => {
        return {
            min: {
                days: policyConfig.documentDeadline?.minInterval?.days,
                years: policyConfig.documentDeadline?.minInterval?.years,
                backward: policyConfig.documentDeadline?.minInterval?.backward ?? false,
                basedOn: policyConfig.documentDeadline?.minIntervalBasedOn,
            },
            max: {
                days: policyConfig.documentDeadline?.maxInterval?.days,
                years: policyConfig.documentDeadline?.maxInterval?.years,
                backward: policyConfig.documentDeadline?.maxInterval?.backward ?? false,
                basedOn: policyConfig.documentDeadline?.maxIntervalBasedOn,
            },
        };
    }, [policyConfig.documentDeadline]);
    const handleWorkflowChange = (workflowId) => {
        onChange({
            ...policyConfig,
            workflowId: workflowId ? Number(workflowId) : undefined,
        });
    };
    const handleEnableToggle = (prop) => (e) => {
        onChange({
            ...policyConfig,
            [prop]: e.target.checked,
        });
    };
    const handleDeadlineChange = (deadlineProp, deadline, isMin) => {
        onChange({
            ...policyConfig,
            [deadlineProp]: {
                ...policyConfig[deadlineProp],
                ...(isMin
                    ? {
                        minInterval: {
                            days: deadline.days ? Number(deadline.days) : undefined,
                            years: deadline.years ? Number(deadline.years) : undefined,
                            backward: deadline.backward,
                        },
                        minIntervalBasedOn: deadline.basedOn,
                    }
                    : {
                        maxInterval: {
                            days: deadline.days ? Number(deadline.days) : undefined,
                            years: deadline.years ? Number(deadline.years) : undefined,
                            backward: deadline.backward,
                        },
                        maxIntervalBasedOn: deadline.basedOn,
                    }),
            },
        });
    };
    const handleReminderChange = (e) => {
        const reminderDays = e.target.value;
        onChange({
            ...policyConfig,
            documentDeadline: {
                ...policyConfig.documentDeadline,
                reminderDays: reminderDays ? Number(reminderDays) : undefined,
            },
        });
    };
    const renderToggle = (prop, label, tooltip) => {
        return (_jsx(FormControlLabel, { control: _jsx(Checkbox, { disabled: readonly, checked: policyConfig[prop] ?? false, onChange: handleEnableToggle(prop) }), label: _jsxs("div", { className: 'flex items-center gap-1', children: [label, tooltip && _jsx(InfoPopover, { content: tooltip })] }) }));
    };
    return (_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx("div", { className: 'mb-2 flex flexJustify--space_between flexItems--center', children: _jsx(Typography, { variant: 'label1', children: t(`WorkflowType.${camelCasedType}`) }) }), includeDeadline && (_jsxs("div", { children: [renderToggle('enableDeadline', t('Enable deadline')), policyConfig.enableDeadline && (_jsxs("div", { className: 'ml-8 mb-4 flex flex-col gap-3', children: [_jsx(DeadlineInput, { label: t('From'), value: deadlineConfig.min, onChange: deadline => handleDeadlineChange('deadline', deadline, true), includeStart: includeTermStart, includeEnd: true, readonly: readonly, className: 'my-2' }), _jsx(DeadlineInput, { label: t('To'), value: deadlineConfig.max, onChange: deadline => handleDeadlineChange('deadline', deadline, false), includeStart: includeTermStart, includeEnd: true, readonly: readonly })] }))] })), _jsxs("div", { children: [renderToggle('enableWorkflow', t('Enable workflow')), policyConfig.enableWorkflow &&
                        ((readonly && !policyConfig.workflowId && t('Not Set')) || (_jsx("div", { className: 'ml-8 mb-4', children: _jsx(DropdownSelect, { disabled: readonly, label: t('Workflow'), onChange: e => handleWorkflowChange(e.selection?.value), options: workflowOptions, value: policyConfig.workflowId }) })))] }), includeOptionalDocument && (_jsx("div", { children: renderToggle('optionalDocument', t('Optional initial document upload'), t('Final documentation for payment request is always required.')) })), includeDocumentDeadline && (_jsxs("div", { children: [renderToggle('enableDocumentDeadline', t('Enable document deadline')), policyConfig.enableDocumentDeadline && (_jsxs("div", { className: 'ml-8 mb-4 flex flex-col gap-3', children: [_jsx(DeadlineInput, { label: t('From'), value: documentDeadlineConfig.min, onChange: deadline => handleDeadlineChange('documentDeadline', deadline, true), includeStart: true, includeEnd: true, readonly: readonly, className: 'my-2' }), _jsx(DeadlineInput, { label: t('To'), value: documentDeadlineConfig.max, onChange: deadline => handleDeadlineChange('documentDeadline', deadline, false), includeEnd: true, includeStart: true, readonly: readonly }), _jsx(TextInput, { label: t('Reminder'), additionalInfo: t('Days before the deadline to send a reminder'), value: policyConfig.documentDeadline?.reminderDays || '', onChange: handleReminderChange, className: 'w-36', append: t('Days'), disabled: readonly }), renderToggle('enableCourseDropoff', t('Enable course dropoff'), t('Enable course dropoff description'))] }))] }))] }));
};
